import { Delete, Edit } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';
import {
  automationTypeOptionsMap,
  type IAutomation,
  isRequirementAutomation,
  isProgressAutomation,
  isStateAutomation,
} from '../../../Interfaces/ILegalTransactionMeasure';
import {
  isLegalRequirement,
  type ILegalRequirement,
} from '../../../Interfaces/ILegalRequirement';
import type { ILegalRequirementTemplate } from '../../../Interfaces/ILegalRequirementTemplate';
import type { ILegalRequirementState } from '../../../Interfaces/ILegalRequirementState';
import type { ILegalTransactionProgress } from '../../../Interfaces/ILegalTransactionProgress';
import type { ILegalTransactionState } from '../../../Interfaces/ILegalTransactionState';
import { useMemo } from 'react';

type IProps = {
  automation: IAutomation;
  legalRequirements: Array<ILegalRequirement>;
  legalRequirementTemplates: Array<ILegalRequirementTemplate>;
  legalRequirementStates: ILegalRequirementState[];
  legalTransactionProgresses: ILegalTransactionProgress[];
  legalTransactionStates: ILegalTransactionState[];
  onDelete: (automationId: number) => void;
  onEdit: (automation: IAutomation) => void;
};

export const AutomationCard = ({
  automation,
  legalRequirements,
  legalRequirementTemplates,
  legalRequirementStates,
  legalTransactionProgresses,
  legalTransactionStates,
  onDelete,
  onEdit,
}: IProps) => {
  const label = useMemo(() => {
    if (isRequirementAutomation(automation)) {
      let requirement:
        | ILegalRequirement
        | ILegalRequirementTemplate
        | undefined;
      if (automation.idLegalRequirement) {
        requirement = legalRequirements.find(
          l => l.idLegalRequirement === automation.idLegalRequirement
        );
      } else if (automation.idLegalRequirementTemplate) {
        requirement = legalRequirementTemplates.find(
          l =>
            l.idLegalRequirementTemplate ===
            automation.idLegalRequirementTemplate
        );
      }

      const requirementState = legalRequirementStates.find(
        l => l.idLegalRequirementState === automation.idLegalRequirementState
      );

      return isLegalRequirement(requirement!) ? (
        <span>
          Setze <b>{requirement?.LegalRequirement}</b> auf Status:{' '}
          <b>{requirementState?.LegalRequirementState}</b>
        </span>
      ) : (
        <span>
          Setze <b>{requirement?.LegalRequirementTemplate}</b> auf Status:{' '}
          <b>{requirementState?.LegalRequirementState}</b>
        </span>
      );
    } else if (isStateAutomation(automation)) {
      const legalTransactionState = legalTransactionStates.find(
        l => l.idLegalTransactionState === automation.idLegalTransactionState
      );

      return (
        <span>
          Setze Vorgang auf Phase:{' '}
          <b>{legalTransactionState?.LegalTransactionState}</b>
        </span>
      );
    } else if (isProgressAutomation(automation)) {
      const legalTransactionProgress = legalTransactionProgresses.find(
        l =>
          l.idLegalTransactionProgress === automation.idLegalTransactionProgress
      );

      return (
        <span>
          Setze Vorgang auf Status:{' '}
          <b>{legalTransactionProgress?.LegalTransactionProgress}</b>
        </span>
      );
    }

    return 'Unbekannt';
  }, [
    automation.AutomationExecutionType,
    legalRequirements,
    legalRequirementStates,
    legalTransactionProgresses,
    legalTransactionStates,
  ]);

  return (
    <Card variant='outlined' key={automation.idAutomation}>
      <CardContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          '&:last-child': {
            // Add this to remove bottom padding
            pb: 2,
          },
        }}
      >
        <Box display='flex' gap={2} alignItems='center'>
          <Typography variant='subtitle1'>
            Typ:{' '}
            {automationTypeOptionsMap[automation.idAutomationExecutionType]}
          </Typography>
          <Chip size='small' label={label} />
        </Box>
        <Box display='flex' gap={2}>
          <IconButton onClick={() => onEdit(automation)}>
            <Edit />
          </IconButton>
          <IconButton onClick={() => onDelete(automation.idAutomation)}>
            <Delete />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};
